'use client';

import { fetchPointsMallInfo } from '@/api/account';
import { IPointsMallCouponDetail, IPointsMallInfo } from '@/api/types/account';
import { IProductDetail } from '@/api/types/public';
import FixedBottomButtonPointsMall from '@/app/[variants]/account/redeemable-items/components/RedeemedDetailDialog/FixedBottomButtonPointsMall';
import RedemptionNotice from '@/app/[variants]/account/redeemable-items/components/RedeemedDetailDialog/RedemptionNotice';
import Breadcrumbs from '@/components/Breadcrumbs';
import { handleTopCarouselData } from '@/components/GoodsDetail/handleData';
import LenseAvailable from '@/components/GoodsDetail/LensesAndCoatingsWithPopup';
import ProductDescriptionCollapse from '@/components/GoodsDetail/ProductDescriptionCollapse';
import ProductDetailsCollapse from '@/components/GoodsDetail/ProductDetailsCollapse';
import TopCarousel from '@/components/GoodsDetail/TopCarousel';
import { Box } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import BenefitTerms from '@/app/[variants]/account/points-mall/components/CouponRedeemDialog/BenefitTerms';
import HowItWorks from '@/app/[variants]/account/points-mall/components/CouponRedeemDialog/HowItWorks';
import RedemptionTerms from '@/app/[variants]/account/points-mall/components/CouponRedeemDialog/RedemptionTerms';
import Coupon from '@/components/Coupon/coupon';
import { ICouponProps } from '@/components/Coupon/type';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import FixedBottomButton from '../../points-mall/components/CouponRedeemDialog/FixedBottomButton';
import ProductBasicInfo from './components/ProductBasicInfo';
export default function RedeemableDetailPageClient({
  productDetail,
  freeData,
  isProductDetail,
  handledCouponData,
  couponDetail,
}: {
  productDetail: IProductDetail;
  freeData: IPointsMallInfo;
  isProductDetail?: boolean;
  handledCouponData: ICouponProps;
  couponDetail: IPointsMallCouponDetail | any;
}) {
  const t = useTranslations('account.pointsMallDetail.pageClient');
  const router = useRouter();
  const { trackEvent } = useReporting();
  let detail = null;
  let showTabBtn = false;
  let handledData = null;
  if (isProductDetail) {
    handledData = handleTopCarouselData(productDetail);
    detail = handledData.productDetail;
    showTabBtn = handledData.showTabBtn;
  }
  useEffect(() => {
    if (isProductDetail && productDetail?.id) {
      trackEvent(
        {
          eventName: 'PointsMallProductDetailsPage_View',
          properties: {
            product_class_name: productDetail?.products_category,
            is_product_lens: productDetail?.is_glass === 1 && detail?.product_lens === 1,
            shop_sku: productDetail?.shop_sku,
            business_sku: productDetail?.business_sku,
            product_sku: productDetail?.sku,
            product_id: `${productDetail?.id}`,
            product_name: productDetail?.name,
          },
        },
        [ProviderType.SENSORS],
      );
    }
  }, [productDetail, isProductDetail]);

  const [currentFreeData, setCurrentFreeData] = useState<IPointsMallInfo>(freeData);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  const refreshFreeData = async () => {
    await refreshAccountInfo();
    const res = await fetchPointsMallInfo();
    setCurrentFreeData(res.data);
  };

  const glassesFilter = () => {
    try {
      let word = '';
      if (detail.short_name) {
        if (detail.is_glass === 1) {
          word = ` ${t('glasses')}`;
        }
        if (detail.is_glass === 1 && detail.is_sunglasses === 1) {
          word = ` ${t('sunglasses')}`;
        }
      }
      return word;
    } catch {
      return '';
    }
  };
  const breadLastLabel = isProductDetail
    ? `${detail?.short_name}${glassesFilter()}`
    : `${couponDetail?.model_type === 4 ? `${t('shipping')} ` : ''}${t('coupon')}`;
  return (
    <Box>
      <Box sx={{ position: 'relative' }}>
        <Breadcrumbs
          items={[
            {
              label: t('home'),
              href: '/',
              onClick: () => {
                router.push('/');
              },
            },
            { label: t('rewardsStore'), href: '/account/points-mall' },
            { label: breadLastLabel, href: '' },
          ]}
          className="absolute top-[10px] left-[15px] z-10"
        />
        {isProductDetail ? (
          <TopCarousel productDetail={detail} showTabBtn={showTabBtn} showTryOn={false} />
        ) : (
          <div className="w-full aspect-square px-14 flex items-center justify-center bg-auxiliary-light-gray">
            <div className="w-full h-auto">
              <Coupon
                id={couponDetail.id}
                couponType={handledCouponData.couponType}
                couponUIType={handledCouponData.couponUIType}
                disCountText={handledCouponData.disCountText}
                title={handledCouponData.title}
                description={handledCouponData.description}
                buttonText={handledCouponData.buttonText}
                copyWriting={handledCouponData.copyWriting}
                isPointsMall
              />
            </div>
          </div>
        )}
        <ProductBasicInfo
          detail={detail}
          couponDetail={couponDetail}
          glassesFilter={glassesFilter}
          pointsMallInfo={currentFreeData}
          isProductDetail={isProductDetail}
        />
        {/* divider */}
        <Box sx={{ height: '10px', backgroundColor: '#F5F6F7' }} />
        <Box
          sx={{
            padding: '0 14px',
            '& .goods-detail-collapse': {
              borderColor: '#EEEEEE',
              '&:first-of-type': {
                borderTop: 'none',
              },
            },
          }}
        >
          {isProductDetail ? (
            <>
              <RedemptionNotice detail={detail} />
              {/* lense available */}
              <LenseAvailable detail={detail} />
              {/* product details */}
              {detail.is_glass === 1 && <ProductDetailsCollapse detail={detail} />}
              {/* product description */}
              {detail.is_glass === 2 && detail.show_description && (
                <ProductDescriptionCollapse detail={detail} />
              )}
            </>
          ) : (
            <>
              <RedemptionTerms couponInfo={couponDetail} />
              <BenefitTerms couponInfo={couponDetail} />
              <HowItWorks />
            </>
          )}
        </Box>
        {/* 空白占位 */}
        <Box sx={{ height: '70px' }} />
        {isProductDetail && (
          <FixedBottomButtonPointsMall
            detail={detail}
            pointsMallData={currentFreeData}
            refreshFreeData={refreshFreeData}
            handledData={handledData}
            containerStyle={{
              padding: '14px 14px',
              boxShadow: '0px -1px 5px 0px rgba(204,204,204,0.5)',
              justifyContent: 'center',
            }}
            buttonStyle={{
              width: 'calc(100% - 32px)',
              height: '42px',
              borderRadius: '22px',
            }}
          />
        )}
        {!isProductDetail && (
          <FixedBottomButton
            detail={couponDetail}
            pointsMallData={currentFreeData}
            refreshFreeData={refreshFreeData}
            containerStyle={{
              padding: '14px 0',
              boxShadow: '0px -1px 5px 0px rgba(204,204,204,0.5)',
              justifyContent: 'center',
            }}
            buttonStyle={{
              width: 'calc(100% - 32px)',
              height: '42px',
              borderRadius: '22px',
            }}
          />
        )}
      </Box>
    </Box>
  );
}
